import { Component, OnInit, ViewChild } from "@angular/core";
import { IonContent, Platform, PopoverController } from "@ionic/angular";
import { ApiService } from "src/app/services/api.service";
import { PopoverSocialShareComponent } from "src/app/components/popover-social-share/popover-social-share.component";
import { SeoService } from "src/app/services/seo.service";
import { PostCategory, PostItem } from "src/app/types/post";
import { ActivatedRoute } from "@angular/router";
import { ImageInterface } from "src/app/types/image";

import { DatePipe } from "@angular/common";
import { SEOSchema } from "src/app/types/seo";
import { JsonLdService } from "src/app/services/json-ld.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-events-detail",
  templateUrl: "./events-detail.page.html",
  styleUrls: ["./events-detail.page.scss"],
})
export class EventsDetailPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  xhrInProgress: boolean = false;
  sameDay: boolean = false;
  startDate: string | number;
  endDate: string | number;
  publishedAt: string | number;
  featuredImage: ImageInterface;
  data: PostItem;
  relatedContent: PostItem[];
  currentCategory: PostCategory;
  ctaUrl: string;
  schema: SEOSchema = {
    "@context": "http://schema.org",
    "@type": "Event",
  };

  constructor(
    private platform: Platform,
    private API: ApiService,
    private route: ActivatedRoute,
    private seo: SeoService,
    public popoverController: PopoverController,
    private datePipe: DatePipe,
    private jsonLdService: JsonLdService
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is("cordova");
    });
  }

  /**
   * Present social share popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: PopoverSocialShareComponent,
      event: ev,
      translucent: true,
      cssClass: "social-popover",
    });
    return await popover.present();
  }

  ngOnInit() {
    this.route.data.subscribe(
      (response: { data: { event: PostItem; relatedContent: PostItem[] } }) => {
        if (response.data.event.publishedAt) {
          this.publishedAt = Date.parse(
            response.data.event.publishedAt.replace(/ /g, "T")
          );
        }

        if (response.data.event.featuredImage) {
          this.featuredImage = {
            id: response.data.event.featuredImageId,
            ratio: response.data.event.featuredImageSelectedRatio,
            url: response.data.event.featuredImage,
            stretched: false,
            withBackground: false,
          };
        }

        if (typeof response.data.event.category === "string") {
          response.data.event.category = JSON.parse(
            response.data.event.category
          );
        }

        if (response.data.event.category?.child?.title) {
          this.currentCategory = response.data.event.category.child;
        } else {
          this.currentCategory = response.data.event.category;
        }

        // Set blog data
        this.data = response.data.event;

        if (this.data.typeSettings?.ctaUrl) {
          if (this.data.typeSettings.ctaUrl.indexOf("://") < 0) {
            this.ctaUrl = "//" + this.data.typeSettings.ctaUrl;
          } else {
            this.ctaUrl = this.data.typeSettings.ctaUrl;
          }
        }

        // Only show startDate if it matches endDate
        if (
          this.data.typeSettings?.startDate &&
          this.data.typeSettings?.endDate
        ) {
          this.startDate = Date.parse(
            this.data.typeSettings.startDate.replace(/ /g, "T")
          );
          this.endDate = Date.parse(
            this.data.typeSettings.endDate.replace(/ /g, "T")
          );
        }

        if (
          this.startDate &&
          this.endDate &&
          this.datePipe.transform(this.startDate, "yyyy-MM-dd") ===
            this.datePipe.transform(this.endDate, "yyyy-MM-dd")
        ) {
          this.sameDay = true;
        }

        if (response.data.relatedContent)
          this.relatedContent = response.data.relatedContent;

        // Set Meta & Social tags
        if (!this.cordova) {
          let schemaData = { ...this.schema };

          if (this.featuredImage?.url) {
            schemaData = {
              ...schemaData,
              image: this.featuredImage.url,
            };
          }

          if (this.data.typeSettings?.startDate) {
            schemaData = {
              ...schemaData,
              startDate: this.datePipe.transform(
                Date.parse(this.data.typeSettings.startDate.replace(/ /g, "T")),
                "yyyy-MM-dd"
              ),
            };
          }

          schemaData = {
            ...schemaData,
            name: this.data.seo.title,
            location: {
              "@type": "Place",
              name:
                this.data.typeSettings.relatedText &&
                this.data.typeSettings.relatedText.length > 0
                  ? this.data.typeSettings.relatedText
                  : this.data.eventContext?.title
                  ? this.data.eventContext.title
                  : "Centre-Wide",
              address: {
                "@type": "PostalAddress",
                streetAddress: environment.street_address,
                addressLocality: environment.address_locality,
                postalCode: environment.postal_code,
                addressCountry: environment.address_country,
              },
            },
          };

          // Title tag
          if (this.data.seo?.title) {
            this.seo.setTitle(this.data.seo.title);
            schemaData = {
              ...schemaData,
              name: this.data.seo.title,
            };
          } else {
            this.seo.setTitle(this.data.title);
            schemaData = {
              ...schemaData,
              name: this.data.title,
            };
          }

          // Meta tags
          if (this.data.seo) {
            this.seo.setMeta(this.data.seo);
            this.seo.setRobotsMeta(this.data.seo);

            schemaData = {
              ...schemaData,
              description: this.data.seo.description,
            };
          }

          // Social tags
          if (this.data.social) {
            if (!this.data.social.socialImage && this.data.socialImage)
              this.data.social.socialImage = this.data.socialImage;

            this.seo.setSEOMeta(this.data.social);
          }

          // Author
          if (this.data.createdBy) {
            this.seo.setAuthor(this.data.createdBy);
          }

          // Canonical URL
          if (this.data.seo?.canonical_url) {
            this.seo.setCanonicalURL(this.data.seo);
          }

          this.jsonLdService.insertSchema(schemaData);
        }
      }
    );
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
